'use client';

import { faSliders } from '@fortawesome/pro-light-svg-icons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@shared/components/ui/dropdown-menu';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useIsMobile } from '@shared/hooks/useMobile';
import { cn } from '@shared/utils/utils';
import { useMemo, useRef } from 'react';
import { SimpleFilterContent } from './SimpleFilterContent';
import type { SimpleFilterProps } from './types';

/**
 * Componente SimpleFilter - Filtros básicos simplificados
 *
 * Componente para manejar filtros simples sin la complejidad de AdvancedFilter.
 * Ideal para casos donde solo necesitas filtros básicos como texto, selects, fechas y checkboxes.
 *
 * Características:
 * - Soporte para múltiples tipos de campos (text, select, date, date-range, checkbox)
 * - Botón de limpiar filtros automático
 * - Indicador de carga
 * - Diseño responsive
 * - Manejo de estado local para campos de texto
 * - Modo modal o inline
 * - Badge con contador de filtros activos
 *
 * @example
 * ```tsx
 * // Modo inline (por defecto)
 * <SimpleFilter
 *   fields={[...]}
 *   values={filters}
 *   onChange={handleFiltersChange}
 * />
 *
 * // Modo modal
 * <SimpleFilter
 *   useModal={true}
 *   fields={[...]}
 *   values={filters}
 *   onChange={handleFiltersChange}
 * />
 * ```
 */
export const SimpleFilter = ({
  fields,
  values,
  onChange,
  onReset,
  isLoading = false,
  loadingText = 'Buscando...',
  showClearButton = true,
  clearButtonText = 'Limpiar',
  className = '',
  translations = {},
  useModal = false,
  triggerText = 'Filtros',
}: SimpleFilterProps) => {
  const isMobile = useIsMobile();
  const modalRef = useRef<ModalCenterRef>(null);

  // 🎯 Contar filtros activos
  const activeFiltersCount = useMemo(() => {
    return fields.filter((field) => {
      const value = values[field.key];
      if (value === undefined || value === null || value === '') return false;
      if (field.type === 'select' && field.allOption && value === field.allOption.value) return false;
      if (field.type === 'checkbox' && !value) return false;
      return true;
    }).length;
  }, [fields, values]);

  // 🎯 Si no es modal, renderizar inline
  if (!useModal) {
    return (
      <div className={`mb-4 bg-card border border-border rounded-lg p-4 ${className}`}>
        <SimpleFilterContent
          fields={fields}
          values={values}
          onChange={onChange}
          onReset={onReset}
          isLoading={isLoading}
          loadingText={loadingText}
          showClearButton={showClearButton}
          clearButtonText={clearButtonText}
          translations={translations}
        />
      </div>
    );
  }

  // 🎯 Modo modal - Mobile usa ModalCenter, Desktop usa Dialog
  if (isMobile) {
    return (
      <>
        {/* Botón trigger para mobile */}
        <div className="relative inline-block">
          <Button
            variant="outline"
            type="button"
            size="sm"
            className={cn(
              'shadow-none transition-colors border border-card-border !bg-body-bg !text-icons hover:border-card-border relative',
            )}
            icon={faSliders}
            iconPosition="start"
            onClick={() => modalRef.current?.openModal()}
            data-testid="simple-filter-open-button-mobile"
          >
            {/* Badge de contador de filtros activos */}
            {activeFiltersCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-sm dark:bg-blue-400 dark:text-blue-900 z-10">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </div>

        {/* Modal para mobile */}
        <ModalCenter
          ref={modalRef}
          title={translations.filters || triggerText}
          type="default"
          width="xl"
          className="w-full h-full max-w-none"
        >
          <SimpleFilterContent
            fields={fields}
            values={values}
            onChange={onChange}
            onReset={onReset}
            isLoading={isLoading}
            loadingText={loadingText}
            showClearButton={showClearButton}
            clearButtonText={clearButtonText}
            translations={translations}
          />
        </ModalCenter>
      </>
    );
  }

  // 🎯 Modo modal - Desktop usa DropdownMenu (como AdvancedFilter)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative inline-block">
          <Button
            variant="outline"
            type="button"
            size="sm"
            className={cn(
              'shadow-none transition-colors border border-card-border !bg-body-bg !text-icons hover:border-card-border relative',
            )}
            icon={faSliders}
            iconPosition="start"
            data-testid="simple-filter-open-button-desktop"
          >
            <span className="hidden sm:block text-sm">{translations.filters || triggerText}</span>
            {/* Badge de contador de filtros activos */}
            {activeFiltersCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-sm dark:bg-blue-400 dark:text-blue-900 z-10">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        className="sm:min-w-[500px] sm:max-w-[500px] p-4 bg-body-bg text-foreground border border-card-border"
      >
        <div className="flex items-center justify-between mb-2 pt-1">
          <div className="text-sm font-semibold text-foreground">{translations.filters || triggerText}</div>
        </div>
        <SimpleFilterContent
          fields={fields}
          values={values}
          onChange={onChange}
          onReset={onReset}
          isLoading={isLoading}
          loadingText={loadingText}
          showClearButton={showClearButton}
          clearButtonText={clearButtonText}
          translations={translations}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
